/**
 * SIM/upload agent studio (seed) — the distinct operator surface for the
 * plant-PC agent, deliberately NOT the customer's operational portal. Pick an
 * equipment, author its event structure (states / events / triggers), see it on
 * a timeline, and run it in isolation (drive the signals, watch state derive).
 * It reuses the same model + API as the portal but is its own focused workspace;
 * data sources, Excel upload and box commissioning land in this surface next.
 *
 * Editing rides the existing OperatingModesCard -> OperatingModesEditor, saved
 * through the whole-entity upsert (POST /v1/twin/entities). The save is applied
 * optimistically to the entities cache so the structure card AND the runner pick
 * up the edit immediately (the runner re-keys on the model signature and resets).
 */

import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, RadioTower, Workflow } from "lucide-react";

import type { EntityDef } from "@foundry/foundry-plant";
import { EquipmentStatePanel } from "@/components/twin/EquipmentStatePanel";
import { OperatingModesCard } from "@/components/twin/OperatingModesCard";
import { OperatingModesRunner } from "@/components/twin/OperatingModesRunner";
import { TWIN_ENTITIES_KEY, useActivePlantQuery, useTwinEntitiesQuery } from "@/lib/api/queries";
import { plantNodeToCreate, toPlantNode, twinEntities } from "@/lib/api/twin-entities";
import { twinPlants } from "@/lib/api/twin-plants";
import type { TwinEntityList } from "@/lib/api/types";
import { useOrg } from "@/lib/org-context";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";

/** A stable signature of the parts of the model the runner depends on, so an edit resets the run. */
function modelSignature(entity: EntityDef): string {
  const om = entity.operating_modes;
  if (!om) return "none";
  const modes = (om.modes ?? []).join(",");
  const transitions = (om.transitions ?? [])
    .map((t) => `${t.on_event}>${t.to}:${t.trigger?.predicate ?? ""}:${t.trigger?.level ?? ""}`)
    .join("|");
  return `${modes}#${transitions}#${om.current_mode ?? ""}`;
}

/**
 * Plant-level "run over the wire": set the plant's sim_mode so the real
 * sim-driver drives it. "live" makes the driver stream raw tags over the broker
 * for every mode-bearing equipment, so equipment_state walks like a real gateway
 * (rehearsal / demo data, walled off from real tenants). This bridges the studio
 * to the actual driver — the in-browser runner is a preview; this is the wire.
 */
function LiveSimControl({ canWrite }: { canWrite: boolean }) {
  const plantQuery = useActivePlantQuery();
  const simMode = plantQuery.data?.sim_mode ?? "none";
  const isLive = simMode === "live" || simMode === "both";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await twinPlants.setSimMode(isLive ? "none" : "live");
      await plantQuery.refetch();
    } catch (e) {
      console.error("sim-studio: set sim_mode failed", e);
      setError(
        "Could not change live simulation — the server may have refused (a real org is never driven, or there is no provisioned plant).",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-surface px-3 py-2.5">
      <RadioTower className={isLive ? "size-5 text-success" : "size-5 text-on-surface-muted"} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-medium text-on-surface">
          Live simulation (over the wire)
          <Badge
            variant={isLive ? "success" : "secondary"}
            className="px-1.5 py-0 font-mono text-[11px]"
          >
            {simMode}
          </Badge>
        </div>
        <p className="text-xs text-on-surface-muted">
          {isLive
            ? "The sim-driver is driving this plant's mode-bearing equipment over the broker — equipment_state walks like a real gateway (rehearsal / demo data)."
            : "Off. Enable and the sim-driver drives the whole plant's equipment over the broker (rehearsal / demo orgs only); it may take a moment to pick up."}
        </p>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
      {canWrite && (
        <Button
          size="sm"
          variant={isLive ? "outline" : "primary"}
          disabled={busy || plantQuery.isLoading}
          onClick={toggle}
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <RadioTower className="size-3.5" />
          )}
          {isLive ? "Stop" : "Run over the wire"}
        </Button>
      )}
    </div>
  );
}

/** The agent's event-structure studio: a picker + the structure view/editor + the isolation runner. */
export function SimStudio() {
  const { can } = useOrg();
  const canWrite = can("model.write");
  const qc = useQueryClient();

  const q = useTwinEntitiesQuery();
  const nodes = useMemo(() => (q.data?.items ?? []).map(toPlantNode), [q.data]);
  const modeled = useMemo(
    () =>
      nodes
        .filter((n) => (n.entity.operating_modes?.modes?.length ?? 0) > 0)
        .sort((a, b) => a.instanceName.localeCompare(b.instanceName)),
    [nodes],
  );

  const [selectedId, setSelectedId] = useState<string | undefined>();
  const selected = modeled.find((n) => n.id === selectedId) ?? modeled[0];

  /**
   * Persist an edited entity: optimistically patch the entities cache (so the
   * card + runner reflect it at once), fire the upsert, then reconcile with the
   * server. plantNodeToCreate round-trips the whole `data` blob, preserving the
   * operating_modes superset fields (archetype_id, id/note, non_mode_events).
   */
  const handleSave = (nextEntity: EntityDef) => {
    if (!selected) return;
    const body = plantNodeToCreate({ ...selected, entity: nextEntity });
    qc.setQueryData<TwinEntityList | undefined>(TWIN_ENTITIES_KEY, (prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) =>
              it.id === body.id
                ? { ...it, kind: body.kind, name: body.name, template_id: body.template_id, data: body.data }
                : it,
            ),
          }
        : prev,
    );
    void twinEntities
      .create(body)
      .catch((err) => console.error("sim-studio: save failed", err))
      .finally(() => void qc.invalidateQueries({ queryKey: TWIN_ENTITIES_KEY }));
  };

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold text-on-surface">Event structure studio</h1>
        <p className="text-sm text-on-surface-muted">
          Author and rehearse each equipment&apos;s states, events and triggers, then run it in
          isolation. This is the SIM / upload agent workspace, not the operational portal.
        </p>
      </header>

      <LiveSimControl canWrite={canWrite} />

      {q.isLoading ? (
        <p className="text-sm text-on-surface-muted">Loading equipment…</p>
      ) : modeled.length === 0 ? (
        <p className="text-sm text-on-surface-muted">
          No equipment with a declared event structure in this plant yet.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-[15rem_1fr]">
          <aside className="flex max-h-[72vh] flex-col gap-0.5 overflow-auto rounded-md border border-border p-2">
            <span className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-on-surface-muted">
              Equipment ({modeled.length})
            </span>
            {modeled.map((n) => {
              const active = n.id === selected?.id;
              return (
                <button
                  key={n.id}
                  type="button"
                  aria-pressed={active}
                  className={
                    active
                      ? "flex items-center gap-2 rounded-md bg-brand/10 px-2 py-1.5 text-left text-sm text-on-surface"
                      : "flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-on-surface-muted hover:bg-surface-muted"
                  }
                  onClick={() => setSelectedId(n.id)}
                >
                  <Workflow className="size-3.5 shrink-0" />
                  <span className="truncate">{n.instanceName}</span>
                </button>
              );
            })}
          </aside>

          {selected && (
            <div className="flex min-w-0 flex-col gap-4">
              <OperatingModesCard entity={selected.entity} canWrite={canWrite} onSave={handleSave} />
              {/* LIVE state from the wire (witnessed equipment_state, polled every 5s) — what the
                  driver / a real gateway is actually doing now, distinct from the in-browser preview
                  below. When "Live simulation" is on, this walks as the sim-driver drives it. */}
              <EquipmentStatePanel entityId={selected.id} />
              <OperatingModesRunner
                key={`${selected.id}:${modelSignature(selected.entity)}`}
                entity={selected.entity}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
